import { SearchIcon } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { PageHeader } from '@/shared/components/PageHeader';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import type { LibraryTotals } from '../totals';

/**
 * The Library's opening: the band every main page of a workspace carries
 * since spec 122, with the search field living inside it because searching is
 * what most visits are for. Everything below stays quiet (spec 104 restyle).
 *
 * It was the one strong brand moment in the product, drawn by hand here; it
 * is `PageHeader surface="inverse"` now, so the five bands are one thing and
 * the Library's cannot drift from the others'.
 */
/**
 * The library's three figures as one sentence (spec 105 FR-008).
 *
 * A figure of zero is left unsaid rather than shown as a nought: "0 updates"
 * is the commonest state there is, and printing it every visit teaches the
 * reader to stop reading the line (FR-010).
 */
function totalsSentence(totals: LibraryTotals): string {
  const parts = [`${String(totals.items)} items`];
  if (totals.installed > 0) {
    parts.push(`${String(totals.installed)} installed here`);
  }
  if (totals.updates > 0) {
    parts.push(
      totals.updates === 1
        ? '1 update waiting'
        : `${String(totals.updates)} updates waiting`,
    );
  }
  return parts.join(' · ');
}

export function LibraryMasthead({
  q,
  onSearchChange,
  searchId,
  totals,
  inputRef,
}: {
  q: string;
  onSearchChange: (q: string) => void;
  searchId: string;
  /** Absent while the catalog is still being read. */
  totals?: LibraryTotals;
  /** So the page can put the cursor here when somebody presses `/`. */
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  const phone = usePhoneWidth();
  return (
    <PageHeader
      surface="inverse"
      eyebrow="Workspace"
      title="Library"
      description="Agents, skills and workflows to install into this workspace. Installing copies them in, so local edits stay local."
    >
      <div className="flex max-w-3xl flex-col gap-3">
        {totals && (
          <p
            // Announced politely: the figures change after an install, and a
            // reader who has just installed something is looking elsewhere.
            role="status"
            aria-live="polite"
            className="text-muted-foreground text-xs tabular-nums"
          >
            {totalsSentence(totals)}
          </p>
        )}
        <div className="relative w-full max-w-lg">
          <SearchIcon
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
          />
          <Input
            ref={inputRef}
            id={searchId}
            type="search"
            aria-label="Search the library"
            // The "/" shortcut does nothing on a phone, so it is not offered
            // there (docs/phone.md PH-04, spec 278).
            placeholder={
              phone
                ? 'Search by name or description'
                : 'Search by name or description  ( / )'
            }
            value={q}
            onChange={(event) => onSearchChange(event.target.value)}
            className="h-10 pl-9 text-base sm:text-sm"
          />
        </div>
      </div>
    </PageHeader>
  );
}
